/**
 * TableOfContents — numbered list of the report's visible sections.
 *
 * Each entry is an in-page anchor + a dotted leader. Click handler
 * scrolls smoothly, then briefly rings the
 * target heading so the reader sees the landing point.
 *
 * Mounted by SiteRagReportView immediately after the cover page,
 * before the executive summary. The print-mode stylesheet keeps the
 * cover and TOC on consecutive pages (no page-break-before on
 * `[data-section="cover"]` or `[data-section="toc"]`); every other
 * `[data-section]` element gets a page break before it, so the TOC
 * naturally lands at the start of page 2 of the customer's PDF.
 *
 * Section labels render at level 1 by default. The level-2 indent is
 * reserved for sub-headings if a future report shape introduces them —
 * `tocEntries.deriveTocEntries` only emits level 1 today.
 */
import React from 'react';
import type { SectionEntry } from '../../../lib/tocEntries';
import {
  formatPageNumber,
  normalizePageNumberFormat,
  type ReportPageNumberFormat,
} from '../../../lib/reportLayoutTokens';

export interface TableOfContentsProps {
  entries: SectionEntry[];
  /** Optional override for the section heading. Defaults to "Contents". */
  heading?: string;
  /** CSS order / visibility from Template Studio section layout. */
  style?: React.CSSProperties;
  /** Render the contents list in two balanced columns (two-column presets). Default 1. */
  columns?: 1 | 2;
  /**
   * 1-based page number for each section, keyed by `SectionEntry.id`.
   * When supplied, the dotted leader on each row terminates in the
   * section's page number (a real ToC); when absent (or a given id is
   * missing) the leader renders without a number — the leader is no
   * longer a dangling line into empty space, just unnumbered.
   *
   * The page-number DATA is owned by the paginator: derive it via
   * `resolvePageMap(...)` + `findPageMapEntry(map, entry.id).startPage`
   * (see `src/lib/templatePages/resolvePageMap.ts`) at the call site and
   * pass the `{ [sectionId]: startPage }` map here.
   */
  pageNumbers?: Record<string, number>;
  /**
   * W4 — page-number FORMAT (`presentation.pageNumberFormat`). The SAME
   * token the puppeteer PDF footer and the Word page fields read, so the
   * TOC's printed page numbers match the running-footer glyphs. Only
   * `'roman-front'` changes the glyph (lowercase roman) — `'arabic'` and
   * `'page-of-total'` both render the bare decimal here (a TOC target page
   * has no document total, so "Page N of M" is footer-only). Defaults to
   * `'page-of-total'` ⇒ decimal, byte-identical to the prior `String(page)`.
   */
  pageNumberFormat?: ReportPageNumberFormat;
}

const RING_CLASSES = ['ring-2', 'ring-primary', 'ring-offset-4', 'ring-offset-card'];
const FLASH_DURATION_MS = 1500;

/**
 * FIX-C — a TOC row must fit ONE page line so its dotted leader + page number
 * stay inside the page box (the paged deck clips TOC overflow via
 * `[data-section="toc"]{overflow:hidden}`). Most section labels are short; the
 * outlier is a custom-brief QUESTION entry, whose full-sentence label runs off
 * the page edge (clipped mid-word, no leader/number). Bound the DISPLAYED label
 * here — at the single render layer every TOC path funnels through — so the cap
 * holds no matter which path produced the entry (the slug-keyed question section
 * id bypasses the data-level bound in `customBriefQuestionTocLabel`). The full
 * label is preserved in the row's `title` for hover/AT. Short labels are
 * unchanged (the threshold sits well above any normal section name).
 */
const TOC_LABEL_MAX_CHARS = 56;
function boundTocLabel(label: string): string {
  const trimmed = label.trim();
  if (trimmed.length <= TOC_LABEL_MAX_CHARS) return trimmed;
  return `${trimmed.slice(0, TOC_LABEL_MAX_CHARS - 1).trimEnd()}…`;
}

export const TableOfContents: React.FC<TableOfContentsProps> = ({ entries, heading = 'Contents', style, columns = 1, pageNumbers, pageNumberFormat }) => {
  if (entries.length === 0) return null;
  // W4 — normalise once; only 'roman-front' alters the rendered glyph, so
  // the default / arabic / page-of-total paths keep the prior decimal text.
  const resolvedFormat = normalizePageNumberFormat(pageNumberFormat);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string, label: string) => {
    // Intercept the default anchor jump so we can (a) smooth-scroll
    // and (b) flash a ring around the target heading. If the target
    // isn't in the DOM (defensive — Prompt 1's filter should make
    // this unreachable), fall back to a console warning + native
    // anchor behaviour rather than silently no-op-ing.
    //
    // TNV — also probe `[data-section="<id>"]` as a fallback. Legacy
    // section wrappers historically emitted only `data-section=` for
    // print page-break CSS, not the matching `id=` native HTML
    // anchors require. New wrappers now emit both, but the
    // querySelector fallback keeps us resilient if a future section
    // forgets the `id`.
    const target =
      document.getElementById(id) ??
      document.querySelector<HTMLElement>(`[data-section="${id}"]`);
    if (!target) {
      console.warn(`[TOC] click target "${id}" (${label}) not in DOM — section may not have rendered`);
      return;
    }
    e.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    target.classList.add(...RING_CLASSES);
    window.setTimeout(() => {
      target.classList.remove(...RING_CLASSES);
    }, FLASH_DURATION_MS);
  };

  return (
    <nav
      // HR-QA fix (2026-06-10) — `[break-inside:avoid]` on the whole nav so a
      // TOC that ALMOST fits a PDF page moves intact to the next page instead
      // of orphaning its last row ("19. Appendix" alone atop page 3). Per CSS
      // fragmentation rules an over-page-height box still fragments, so a
      // genuinely long TOC cannot blank a page. Per-row avoid (on each <li>)
      // stays as the inner guard.
      className="my-8 [break-inside:avoid] [-webkit-column-break-inside:avoid]"
      aria-label="Table of contents"
      data-section="toc"
      data-section-id="toc"
      data-testid="report-toc"
      style={style}
    >
      <h2
        id="toc"
        className="text-xl font-bold mb-4 text-text-primary print:text-slate-900 [data-print-export]:text-slate-900"
        style={{ scrollMarginTop: '80px' }}
      >
        {heading}
      </h2>
      <ol
        className="space-y-1.5"
        style={columns === 2 ? { columnCount: 2, columnGap: '2rem', columnFill: 'balance' } : undefined}
      >
        {entries.map((entry, i) => {
          const number = i + 1;
          const page = pageNumbers?.[entry.id];
          const hasPage = typeof page === 'number' && Number.isFinite(page);
          const displayLabel = boundTocLabel(entry.label);
          const labelTruncated = displayLabel !== entry.label.trim();
          return (
            <li
              key={entry.id}
              // FIX-C — `min-w-0` lets the row's flex children shrink below their
              // content width, a defensive guard so a long entry can never force
              // the row past the page measure. The primary FIX-C guarantee is at
              // the data level (`customBriefQuestionTocLabel` bounds the label),
              // so the leader + page number always fit; this keeps a defensive
              // wrap for any other over-long label.
              className={`flex items-baseline gap-2 py-1 min-w-0 [break-inside:avoid] [-webkit-column-break-inside:avoid] ${entry.level === 2 ? 'pl-6 text-sm' : 'text-base'}`}
              data-testid={`toc-entry-${entry.id}`}
            >
              <span className="text-text-secondary text-sm font-mono w-8 shrink-0 text-right tabular-nums">
                {number}.
              </span>
              <a
                href={`#${entry.id}`}
                onClick={(e) => handleClick(e, entry.id, entry.label)}
                // FIX-C — render the BOUNDED label (full text in `title`), and
                // keep `min-w-0 break-words` + `whiteSpace:normal` as a wrapping
                // backstop. Together the leader + page number always stay inside
                // the page box for any entry from any code path.
                title={labelTruncated ? entry.label : undefined}
                className="min-w-0 break-words text-text-primary hover:text-primary print:text-slate-900 [data-print-export]:text-slate-900 underline underline-offset-4 decoration-1 decoration-text-muted/50 hover:decoration-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-sm transition-colors"
                style={{ whiteSpace: 'normal', overflowWrap: 'break-word' }}
                data-testid={`toc-link-${entry.id}`}
              >
                {displayLabel}
              </a>
              <span
                aria-hidden="true"
                className="flex-1 self-end border-b border-dashed border-text-muted/30 mb-1 mx-1"
              />
              {hasPage && (
                <span
                  className="text-text-secondary text-sm shrink-0 tabular-nums"
                  data-testid={`toc-page-${entry.id}`}
                >
                  {formatPageNumber(page, resolvedFormat)}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};
